// Inline-SVG QR renderer over the dependency-free encoder. Real white
// background + black modules regardless of the app's dark theme — scanners
// need genuine contrast, so this deliberately bypasses the design tokens.
// Adds the spec-minimum 4-module quiet zone around the symbol.

import { useMemo, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';
import { encodeQr } from '@merkury/brain/lib/qrEncode.js';

const QUIET_ZONE = 4;

export function QrCode({
  value,
  size = 240,
  onError,
}: {
  value: string;
  size?: number;
  onError?: (err: Error) => void;
}): ReactNode {
  const t = useT();
  const encoded = useMemo(() => {
    try {
      return { matrix: encodeQr(value), error: undefined };
    } catch (err) {
      return { matrix: undefined, error: err instanceof Error ? err : new Error(String(err)) };
    }
  }, [value]);

  if (encoded.error) {
    onError?.(encoded.error);
    return (
      <div role="alert" className="rounded-xl bg-surface p-4 text-sm text-danger">
        {t('qr.error')}
      </div>
    );
  }

  const matrix = encoded.matrix!;
  const modules = matrix.length + QUIET_ZONE * 2;
  // One <path> for all dark modules keeps the SVG tiny even at version 10.
  let d = '';
  for (let r = 0; r < matrix.length; r++) {
    for (let c = 0; c < matrix.length; c++) {
      if (matrix[r]![c]) d += `M${c + QUIET_ZONE} ${r + QUIET_ZONE}h1v1h-1z`;
    }
  }

  return (
    <svg
      role="img"
      aria-label={t('qr.aria', { value })}
      viewBox={`0 0 ${modules} ${modules}`}
      width={size}
      height={size}
      style={{ display: 'block' }}
      shapeRendering="crispEdges"
    >
      <rect width={modules} height={modules} fill="#ffffff" />
      <path d={d} fill="#000000" />
    </svg>
  );
}
